import { createContext, useCallback, useContext, useEffect, useMemo, useState, useSyncExternalStore, type ReactNode } from 'react';

import { BulkContext } from 'pages/Batch/sections/Context/Context';
import { ActionTypes } from 'pages/Batch/sections/Context/types';
import { BatchDataStatus } from 'configs/constants';

import loadingImage from 'utils/images/loadingImage';

import { useRemotePresetShelves } from 'pages/Batch/components/PresetsFeature/useRemotePresetShelves';
import { USER_SHELF_ID, USER_SHELF_TITLE } from 'pages/Batch/components/PresetsFeature/constants';
import type { IPresetShelf } from 'pages/Batch/components/PresetsFeature/constants';

import useWebhookCredits from 'hooks/useWebhookCredits';

import type { IActionsPreset, IPresetStep, IPresetReplayResult, IPresetPreview } from './types';
import { PRESET_SCHEMA_VERSION } from './types';
import { createEmptyPresetReplayResult } from './replayResult';
import { presetSessionRecorder } from './presetSessionRecorder';
import { savePresetToDrive, loadPresetsFromDrive, deletePresetFromDrive, updatePresetOnDrive } from './presetDriveService';
import { replayActionPreset } from './replayActionPreset';
import { replayWebhookPreset } from './replayWebhookPreset';

interface IPresetContextValue {
  currentSteps: IPresetStep[];
  clearRecording: () => void;

  shelves: IPresetShelf[];
  isLoadingPresets: boolean;
  loadPresets: () => Promise<void>;
  savePreset: (name: string, previewImageSrc: string) => Promise<IActionsPreset>;
  updatePreset: (preset: IActionsPreset) => Promise<void>;
  deletePreset: (presetId: string) => Promise<void>;

  isReplaying: boolean;
  replayPresetById: (presetId: string, targetKeys?: string[]) => Promise<IPresetReplayResult>;
}

const PresetContext = createContext<IPresetContextValue | null>(null);

const subscribeToRecorder = (cb: () => void) => presetSessionRecorder.subscribe(cb);
const getRecorderSnapshot = () => presetSessionRecorder.getSnapshot();

export function PresetContextProvider({ children }: { children: ReactNode }) {
  const { selectedData, dispatchData, selectedKeys, setSelectedKeys, data } = useContext(BulkContext);

  const recorderSteps = useSyncExternalStore(subscribeToRecorder, getRecorderSnapshot);

  const [presets, setPresets] = useState<IActionsPreset[]>([]);
  const [isLoadingPresets, setIsLoadingPresets] = useState(false);
  const [isReplaying, setIsReplaying] = useState(false);

  const remoteShelves = useRemotePresetShelves();
  const checkWebhookCreditsForBatch = useWebhookCredits();

  const shelves = useMemo<IPresetShelf[]>(() => {
    const result: IPresetShelf[] = [];

    if (presets.length > 0) {
      result.push({ id: USER_SHELF_ID, title: USER_SHELF_TITLE, userManaged: true, presets });
    }

    remoteShelves.forEach(shelf => {
      result.push({ id: shelf.id, title: shelf.title, userManaged: false, presets: shelf.presets });
    });

    return result;
  }, [presets, remoteShelves]);

  const allPresets = useMemo(() => shelves.flatMap(s => s.presets), [shelves]);

  const clearRecording = useCallback(() => {
    presetSessionRecorder.clearSteps();
  }, []);

  const loadPresets = useCallback(async () => {
    setIsLoadingPresets(true);
    try {
      const loaded = await loadPresetsFromDrive();
      setPresets(loaded);
    } catch (error) {
      console.error('Failed to load presets:', error);
    } finally {
      setIsLoadingPresets(false);
    }
  }, []);

  useEffect(() => {
    loadPresets();
  }, [loadPresets]);

  const savePreset = useCallback(async (name: string, previewImageSrc: string): Promise<IActionsPreset> => {
    const now = new Date().toISOString();

    let preview: IPresetPreview = { url: '', width: 0, height: 0 };

    try {
      const image = await loadingImage(previewImageSrc);
      preview = {
        url: previewImageSrc,
        width: image.naturalWidth,
        height: image.naturalHeight,
      };
    } catch {
      // Fallback to empty preview if image loading fails
    }

    const preset: IActionsPreset = {
      type: 'actions',
      id: crypto.randomUUID(),
      schemaVersion: PRESET_SCHEMA_VERSION,
      name,
      steps: presetSessionRecorder.getSteps(),
      preview,
      createdAt: now,
      updatedAt: now,
    };

    try {
      const saved = await savePresetToDrive(preset);
      setPresets(prev => [saved, ...prev]);
      return saved;
    } catch (error) {
      console.error('Failed to save preset:', error);
      throw error;
    }
  }, []);

  const updatePreset = useCallback(async (preset: IActionsPreset) => {
    const updated = {
      ...preset,
      updatedAt: new Date().toISOString(),
    };
    try {
      const saved = await updatePresetOnDrive(updated);
      setPresets(prev => prev.map(p => (p.id === saved.id ? saved : p)));
    } catch (error) {
      console.error('Failed to update preset:', error);
      throw error;
    }
  }, []);

  const deletePreset = useCallback(
    async (presetId: string) => {
      const preset = presets.find(p => p.id === presetId);
      setPresets(prev => prev.filter(p => p.id !== presetId));
      if (preset?.driveFolderId) {
        try {
          await deletePresetFromDrive(preset.driveFolderId);
        } catch (error) {
          console.error('Failed to delete preset from Drive:', error);
        }
      }
    },
    [presets],
  );

  const replayPresetById = useCallback(
    async (presetId: string, targetKeys?: string[]): Promise<IPresetReplayResult> => {
      const preset = allPresets.find(p => p.id === presetId);
      if (!preset) {
        throw new Error(`Preset not found: ${presetId}`);
      }

      const keysToUse = targetKeys || selectedKeys;
      const dataToUse = targetKeys ? data.filter(item => targetKeys.includes(item.key)) : selectedData;

      if (preset.type === 'webhook') {
        const cleared = await checkWebhookCreditsForBatch(preset.flowId, dataToUse.length);
        if (!cleared) {
          // User saw the offer/registration screen via useSubscribe; no work performed.
          return createEmptyPresetReplayResult();
        }
      }

      if (targetKeys) {
        setSelectedKeys(targetKeys);
      }

      setIsReplaying(true);
      try {
        dispatchData({
          type: ActionTypes.UPDATE_STATUSES,
          status: BatchDataStatus.IN_PROGRESS,
          statusIndicatorMode: 'both',
          skipItemsWithError: false,
          itemKeys: keysToUse,
        });

        const replayResult = preset.type === 'webhook' ? await replayWebhookPreset(preset, dataToUse) : await replayActionPreset(preset, dataToUse);

        replayResult.processedData.forEach(item => {
          dispatchData({ type: ActionTypes.PUT_DATA, item });
        });

        dispatchData({ type: ActionTypes.APPLY_PENDING, selectedPhotoIds: keysToUse });

        return replayResult;
      } catch (error) {
        dispatchData({
          type: ActionTypes.UPDATE_STATUSES,
          status: BatchDataStatus.ERROR,
          statusIndicatorMode: 'both',
          skipItemsWithError: false,
          itemKeys: keysToUse,
        });
        throw error;
      } finally {
        setIsReplaying(false);
      }
    },
    [allPresets, selectedData, data, dispatchData, selectedKeys, setSelectedKeys, checkWebhookCreditsForBatch],
  );

  const value = useMemo(
    (): IPresetContextValue => ({
      currentSteps: recorderSteps,
      clearRecording,
      shelves,
      isLoadingPresets,
      loadPresets,
      savePreset,
      updatePreset,
      deletePreset,
      isReplaying,
      replayPresetById,
    }),
    [recorderSteps, clearRecording, shelves, isLoadingPresets, loadPresets, savePreset, updatePreset, deletePreset, isReplaying, replayPresetById],
  );

  return <PresetContext.Provider value={value}>{children}</PresetContext.Provider>;
}

export function usePresets(): IPresetContextValue {
  const context = useContext(PresetContext);
  if (!context) {
    throw new Error('usePresets must be used within a PresetContextProvider');
  }
  return context;
}
